import { InfoCircleOutlined } from '@ant-design/icons';
import WarningFilled from '@ant-design/icons/WarningFilled';
import { App } from 'antd/lib';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import { formatDistanceToNow } from 'date-fns';
import { PRIMARY_COLOR } from 'src/theme';
import { IPjmMeterDataUpload } from '../../../../../domain/pjm/meter-data-upload/interface';
import { ColumnsType } from 'antd/lib/table/interface';
import { useRecentMeterDataUploadsQuery } from '../../../../../domain/pjm/meter-data-upload/queries';
import { getComputerFormattedTime } from 'src/domain/date/date';

export function RecentPjmRegistrationUploadMeterDataRequestsList() {
    const { notification } = App.useApp();

    const refetchIntervalMs = 30000; // every 30 seconds
    const { data, isFetching, isLoading, isError, error } = useRecentMeterDataUploadsQuery({
        keepPreviousData: true,
        refetchInterval: refetchIntervalMs,
    });

    if (isError) {
        notification.error({ key: 'recent-meter-data-uploads-error', message: error?.message || 'Cannot load data!' });
    }

    function makeStatusTag(status: any) {
        switch (status) {
            case 'done':
                return <Tag color="success">{status}</Tag>;
            case 'failed':
                return <Tag color="error">{status}</Tag>;
            case 'pending':
                return <Tag color="processing">{status}</Tag>;
            default:
                return <Tag color="default">{status}</Tag>;
        }
    }

    function makeWinterPeakLoadTag(isCalculateWinterPeakLoad: boolean) {
        switch (isCalculateWinterPeakLoad) {
            case true:
                return <Tag color="success">Yes</Tag>;
            case false:
                return <Tag color="error">No</Tag>;
        }
    }

    function getErrorMessage(item: IPjmMeterDataUpload) {
        if (item.status !== 'failed') return '';
        let errorMessage = item.error_message;

        return errorMessage || 'Unknown error';
    }

    const columns: ColumnsType<IPjmMeterDataUpload> = [
        {
            title: 'User',
            dataIndex: 'user',
            width: 220,
            render: (text, record) => (
                <>
                    <div>{record.actor}</div>
                    <div style={{ fontSize: '11px' }} title={'Requested: ' + getComputerFormattedTime(record.date_created)}>
                        {formatDistanceToNow(record.date_created)}
                    </div>
                </>
            ),
        },
        {
            title: 'File Name',
            dataIndex: 'file_name',
            width: 350,
            render: (text, record) => record.meter_data_file.original_name,
        },
        {
            title: () => {
                return (
                    <>
                        Calculate WPL
                        <Tooltip title="Should we trigger calculation of Winter Peak Load after uploading a file">
                            <InfoCircleOutlined style={{ color: PRIMARY_COLOR, fontSize: '15px', marginLeft: '8px', cursor: 'pointer' }} />
                        </Tooltip>
                    </>
                );
            },
            dataIndex: 'calculate_wpl',
            width: 140,
            render: (text, record) => makeWinterPeakLoadTag(record.is_calculate_winter_peak_load),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (text, record) => makeStatusTag(record.status),
        },
        {
            title: '',
            dataIndex: 'error_message',
            width: 30,
            render: (text, record) =>
                record.status === 'failed' && (
                    <Tooltip title={getErrorMessage(record)}>
                        <WarningFilled style={{ fontSize: '14px', color: 'orange', cursor: 'pointer' }} />
                    </Tooltip>
                ),
        },
    ];

    return (
        <Table
            key="recent-pjm-meter-data-uploads"
            className="recent-pjm-meter-data-uploads"
            rowKey="column"
            size="small"
            scroll={{ x: 'max-content', y: 400 }}
            sticky
            columns={columns as any}
            dataSource={data}
            pagination={false}
            loading={isFetching || isLoading}
        />
    );
}
